import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPendingActionsSummary } from '../config/api';
import useIsMobile from '../hooks/useIsMobile';
import { hasCrmAccess, hasCrmAdminAccess, hasCrmManagerAccess } from '../utils/crmAccess';

// Short page names for the mobile header ("zun OMS · Orders"). Desktop keeps
// the full "Order Management Portal" brand and shows no page name.
const MOBILE_PAGE_TITLES = [
  ['/orders/create', 'New Order'],
  ['/orders/', 'Order'],
  ['/orders', 'Orders'],
  ['/pending-actions', 'Pending Actions'],
  ['/serial-dcr', 'Serial & DCR'],
  ['/crm/leads/create', 'New Lead'],
  ['/crm/leads/', 'Lead'],
  ['/crm/leads', 'Leads'],
  ['/crm/history', 'CRM History'],
  ['/crm/settings', 'CRM Settings'],
  ['/crm/dashboard', 'CRM Dashboard'],
  ['/crm/calling', 'Calling'],
  ['/products', 'Products'],
  ['/pricing-history', 'Pricing History'],
  ['/pricing', 'Pricing'],
  ['/brand-support', 'Brand Support'],
  ['/kits', 'Kits'],
  ['/access', 'Users'],
  ['/payments', 'Payments'],
  ['/wallet-payments', 'Wallet Payments'],
  ['/live-whatsapp', 'Live WhatsApp'],
  ['/dba', 'Bank Accounts'],
  ['/generated-invoices', 'Invoices'],
  ['/invoice-dashboard', 'Invoice Dashboard'],
  ['/gst', 'GST'],
  ['/promo-wallet', 'Promo Wallet'],
  ['/coupons', 'Coupons'],
  ['/b2b-coupons', 'B2B Coupons'],
  ['/b2b-config', 'B2B Config'],
  ['/b2b-products', 'B2B Products'],
  ['/hub-management', 'Hubs'],
  ['/deals-banners', 'Deals Banners'],
  ['/gift-config', 'Gift Product'],
  ['/whatsapp', 'WhatsApp'],
  ['/notifications', 'Notifications'],
  ['/audiences', 'Audiences'],
];

const mobilePageTitle = (pathname) =>
  (MOBILE_PAGE_TITLES.find(([prefix]) => pathname === prefix || pathname.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`)) || [])[1] || '';

const Navbar = () => {
  const { user, logout } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [ordersDropdownOpen, setOrdersDropdownOpen] = useState(false);
  const [paymentsDropdownOpen, setPaymentsDropdownOpen] = useState(false);
  const [b2bDropdownOpen, setB2bDropdownOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [crmDropdownOpen, setCrmDropdownOpen] = useState(false);
  const [hubsDropdownOpen, setHubsDropdownOpen] = useState(false);
  const [marketingDropdownOpen, setMarketingDropdownOpen] = useState(false);
  const [pendingActionsCount, setPendingActionsCount] = useState(0);

  // On mobile, dropdowns open on tap (hover doesn't exist); on desktop the
  // onMouseEnter/onMouseLeave handlers keep working as before.
  const toggleOnMobile = (setter) => () => {
    if (isMobile) setter((prev) => !prev);
  };

  // A phone tap also fires a synthetic hover just before the click, which
  // would open the dropdown and the click would close it again. So hover
  // only drives the dropdowns on desktop.
  const hoverOnDesktop = (setter, value) => () => {
    if (!isMobile) setter(value);
  };

  // Same as toggleOnMobile, for a dropdown toggle that is itself a <Link>:
  // on mobile the tap opens the dropdown instead of navigating.
  const toggleLinkOnMobile = (setter) => (e) => {
    if (isMobile) {
      e.preventDefault();
      setter((prev) => !prev);
    }
  };

  // Close the mobile menu after an actual navigation (a link tap), not when a
  // dropdown toggle (a <span>, or the Products <Link>) is tapped.
  const handleNavClick = (e) => {
    if (e.target.closest('a') && !e.target.closest('.dropdown-toggle')) setMenuOpen(false);
  };
  
  // Close the mobile menu on any route change, including the phone's Back button.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // The open mobile menu covers the page; stop the page scrolling behind it.
  useEffect(() => {
    document.body.style.overflow = isMobile && menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, menuOpen]);

  // Get user data from localStorage to check role
  const userData = user || {};
  const canManageHubs = userData.role === 'superAdmin' || userData.role === 'admin';
  const canManageDealsBanners = userData.role === 'superAdmin' || userData.access?.includes('marketing');

  useEffect(() => {
    loadPendingActionsCount();
  }, []);

  const loadPendingActionsCount = async () => {
    try {
      const response = await getPendingActionsSummary();
      if (response && response.data) {
        const totalCount = response.data.reduce((sum, item) => sum + item.count, 0);
        setPendingActionsCount(totalCount);
      }
    } catch (error) {
      console.error('Error loading pending actions count:', error);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const HubsDropdown = () => (
    <div
      className="nav-dropdown"
      onMouseEnter={hoverOnDesktop(setHubsDropdownOpen, true)}
      onMouseLeave={hoverOnDesktop(setHubsDropdownOpen, false)}
      style={{ position: 'relative' }}
    >
      <span className="nav-link dropdown-toggle" onClick={toggleOnMobile(setHubsDropdownOpen)}>
        Hubs
      </span>
      {hubsDropdownOpen && (
        <div className="dropdown-menu">
          {canManageHubs && <Link to="/hub-management" className="dropdown-item">Hub Management</Link>}
          {canManageDealsBanners && <Link to="/deals-banners" className="dropdown-item">Deals Banners</Link>}
          {canManageDealsBanners && <Link to="/gift-config" className="dropdown-item">Gift Product</Link>}
        </div>
      )}
    </div>
  );

  // Marketing groups the customer-outreach modules. Each child is gated by the
  // same access rules it had as a standalone link, and the dropdown only renders
  // when the user can see at least one child.
  const isSuperAdmin = userData.role === 'superAdmin';
  const isAdmin = userData.role === 'admin';
  const hasMarketing = userData.access?.includes('marketing');
  const hasPricing = userData.access?.includes('pricing');
  const canSeeWhatsApp = isSuperAdmin || isAdmin || hasMarketing;
  const canSeeNotifications = isSuperAdmin || isAdmin || hasPricing;
  const canSeeAudiences = isSuperAdmin || hasMarketing;
  const canSeeMarketing = canSeeWhatsApp || canSeeNotifications || canSeeAudiences;

  const MarketingDropdown = () => (
    <div
      className="nav-dropdown"
      onMouseEnter={hoverOnDesktop(setMarketingDropdownOpen, true)}
      onMouseLeave={hoverOnDesktop(setMarketingDropdownOpen, false)}
      style={{ position: 'relative' }}
    >
      <span className="nav-link dropdown-toggle" onClick={toggleOnMobile(setMarketingDropdownOpen)}>
        Marketing
      </span>
      {marketingDropdownOpen && (
        <div className="dropdown-menu">
          {canSeeWhatsApp && <Link to="/whatsapp" className="dropdown-item">WhatsApp</Link>}
          {canSeeNotifications && <Link to="/notifications" className="dropdown-item">Notifications</Link>}
          {canSeeAudiences && <Link to="/audiences" className="dropdown-item">Audiences</Link>}
        </div>
      )}
    </div>
  );

  return (
    <nav className="navbar">
      <div className="navbar-content">
        <Link to="/orders" className="navbar-brand">
          {isMobile ? 'zun OMS' : 'Order Management Portal'}
        </Link>
        {isMobile && <span className="navbar-page-title">{mobilePageTitle(location.pathname)}</span>}
        <button
          type="button"
          className="navbar-toggle"
          aria-label="Toggle navigation menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          {menuOpen ? '✕' : '☰'}
        </button>
        <div
          className={`navbar-nav${menuOpen ? ' open' : ''}`}
          onClick={handleNavClick}
        >
          <div 
            className="nav-dropdown"
            onMouseEnter={hoverOnDesktop(setOrdersDropdownOpen, true)}
            onMouseLeave={hoverOnDesktop(setOrdersDropdownOpen, false)}
            style={{ position: 'relative' }}
          >
            <span className="nav-link dropdown-toggle" onClick={toggleOnMobile(setOrdersDropdownOpen)}>
              Orders
            </span>
            {ordersDropdownOpen && (
              <div className="dropdown-menu">
                <Link to="/orders" className="dropdown-item">
                  Orders
                </Link>
                <Link to="/pending-actions" className="dropdown-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Pending Actions</span>
                  {pendingActionsCount > 0 && (
                    <span 
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        borderRadius: '12px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        marginLeft: '8px'
                      }}
                    >
                      {pendingActionsCount}
                    </span>
                  )}
                </Link>
                <Link to="/serial-dcr" className="dropdown-item">
                  Serial Numbers & DCR
                </Link>
              </div>
            )}
          </div>
          {hasCrmAccess(userData) && (
            <div
              className="nav-dropdown"
              onMouseEnter={hoverOnDesktop(setCrmDropdownOpen, true)}
              onMouseLeave={hoverOnDesktop(setCrmDropdownOpen, false)}
              style={{ position: 'relative' }}
            >
              <span className="nav-link dropdown-toggle" onClick={toggleOnMobile(setCrmDropdownOpen)}>
                CRM
              </span>
              {crmDropdownOpen && (
                <div className="dropdown-menu">
                  <Link to="/crm/leads" className="dropdown-item">
                    Leads
                  </Link>
                  <Link to="/crm/calling" className="dropdown-item">
                    Calling view
                  </Link>
                  {hasCrmManagerAccess(userData) && (
                    <Link to="/crm/dashboard" className="dropdown-item">
                      Dashboard
                    </Link>
                  )}
                  <Link to="/crm/leads/create" className="dropdown-item">
                    Add lead
                  </Link>
                  {hasCrmAdminAccess(userData) && (
                      <Link to="/crm/history" className="dropdown-item">
                        Edit history
                      </Link>
                  )}
                  {hasCrmAdminAccess(userData) && (
                      <Link to="/crm/settings" className="dropdown-item">
                        Settings
                      </Link>
                  )}
                </div>
              )}
            </div>
          )}
          <Link to="/live-whatsapp" className="nav-link">
            Live WhatsApp
          </Link>
          {userData.role === 'superAdmin' && (
            <>
            <div 
              className="nav-dropdown"
              onMouseEnter={hoverOnDesktop(setProductsDropdownOpen, true)}
              onMouseLeave={hoverOnDesktop(setProductsDropdownOpen, false)}
              style={{ position: 'relative' }}
            >
              <Link to="/products" className="nav-link dropdown-toggle" onClick={toggleLinkOnMobile(setProductsDropdownOpen)}>
                Products
              </Link>
              {productsDropdownOpen && (
                <div className="dropdown-menu">
                  <Link to="/products" className="dropdown-item">Products</Link>
                  <Link to="/kits" className="dropdown-item">Custom Kits</Link>
                  <Link to="/brand-support" className="dropdown-item">Brand Support</Link>
                  <Link to="/coupons" className="dropdown-item">Coupons</Link>
                  {canManageDealsBanners && <Link to="/gift-config" className="dropdown-item">Gift Product</Link>}
                </div>
              )}
            </div>
            <div 
              className="nav-dropdown"
              onMouseEnter={hoverOnDesktop(setPaymentsDropdownOpen, true)}
              onMouseLeave={hoverOnDesktop(setPaymentsDropdownOpen, false)}
            >
              <span className="nav-link dropdown-toggle" onClick={toggleOnMobile(setPaymentsDropdownOpen)}>
                Payments
              </span>
              {paymentsDropdownOpen && (
                <div className="dropdown-menu">
                  <Link to="/payments" className="dropdown-item">
                    Ledger
                  </Link>
                  <Link to="/wallet-payments" className="dropdown-item">
                    Wallet Payments
                  </Link>
                  <Link to="/dba" className="dropdown-item">
                    Dedicated Bank Accounts
                  </Link>
                  <Link to="/generated-invoices" className="dropdown-item">
                    Invoices
                  </Link>
                  <Link to="/gst" className="dropdown-item">
                    GST
                  </Link>
                  <Link to="/invoice-dashboard" className="dropdown-item">
                    Invoice Dashboard
                  </Link>
                </div>
              )}
            </div>
            <Link to="/access" className="nav-link">
                Users
              </Link>
            <HubsDropdown />
            <div 
              className="nav-dropdown"
              onMouseEnter={hoverOnDesktop(setB2bDropdownOpen, true)}
              onMouseLeave={hoverOnDesktop(setB2bDropdownOpen, false)}
              style={{ position: 'relative' }}
            >
              <span className="nav-link dropdown-toggle" onClick={toggleOnMobile(setB2bDropdownOpen)}>B2B</span>
              {b2bDropdownOpen && (
                <div className="dropdown-menu">
                  <Link to="/b2b-config" className="dropdown-item">Whitelist</Link>
                  <Link to="/b2b-products" className="dropdown-item">Products</Link>
                  <Link to="/b2b-coupons" className="dropdown-item">Coupons</Link>
                </div>
              )}
            </div>
            </>
          )}
          {!canManageHubs && canManageDealsBanners && <HubsDropdown />}
          {userData.role === 'admin' && (
            <>
            <HubsDropdown />
            </>
          )}
          {(userData.access?.includes('marketing') || userData.access?.includes('pricing')) && userData.role != "superAdmin" && (
            <>
              <div 
                className="nav-dropdown"
                onMouseEnter={hoverOnDesktop(setProductsDropdownOpen, true)}
                onMouseLeave={hoverOnDesktop(setProductsDropdownOpen, false)}
                style={{ position: 'relative' }}
              >
                <Link to="/products" className="nav-link dropdown-toggle" onClick={toggleLinkOnMobile(setProductsDropdownOpen)}>
                  Products
                </Link>
                {productsDropdownOpen && (
                  <div className="dropdown-menu">
                    <Link to="/products" className="dropdown-item">Products</Link>
                    <Link to="/kits" className="dropdown-item">Custom Kits</Link>
                    <Link to="/brand-support" className="dropdown-item">Brand Support</Link>
                    {(userData.role === 'superAdmin' || userData.role === 'admin' || userData.access?.includes('pricing')) && (
                      <Link to="/coupons" className="dropdown-item">Coupons</Link>
                    )}
                    {canManageDealsBanners && <Link to="/gift-config" className="dropdown-item">Gift Product</Link>}
                  </div>
                )}
              </div>
            </>
          )}
          {userData.access?.includes('marketing') && userData.role != "superAdmin" && userData.role != "admin" && (
            <>
              <div 
                className="nav-dropdown"
                onMouseEnter={hoverOnDesktop(setB2bDropdownOpen, true)}
                onMouseLeave={hoverOnDesktop(setB2bDropdownOpen, false)}
                style={{ position: 'relative' }}
              >
                <span className="nav-link dropdown-toggle" onClick={toggleOnMobile(setB2bDropdownOpen)}>B2B</span>
                {b2bDropdownOpen && (
                  <div className="dropdown-menu">
                    <Link to="/b2b-config" className="dropdown-item">Whitelist</Link>
                    <Link to="/b2b-products" className="dropdown-item">Products</Link>
                    <Link to="/b2b-coupons" className="dropdown-item">Coupons</Link>
                  </div>
                )}
              </div>
            </>
          )}

          {userData.access?.includes('finance') && userData.role != "superAdmin" && (
            <>
            <div 
              className="nav-dropdown"
              onMouseEnter={hoverOnDesktop(setPaymentsDropdownOpen, true)}
              onMouseLeave={hoverOnDesktop(setPaymentsDropdownOpen, false)}
            >
              <span className="nav-link dropdown-toggle" onClick={toggleOnMobile(setPaymentsDropdownOpen)}>
                Payments
              </span>
              {paymentsDropdownOpen && (
                <div className="dropdown-menu">
                  <Link to="/payments" className="dropdown-item">
                    Ledger
                  </Link>
                  <Link to="/wallet-payments" className="dropdown-item">
                    Wallet Payments
                  </Link>
                  <Link to="/dba" className="dropdown-item">
                    Dedicated Bank Accounts
                  </Link>
                  <Link to="/generated-invoices" className="dropdown-item">
                    Invoices
                  </Link>
                  <Link to="/gst" className="dropdown-item">
                    GST
                  </Link>
                  <Link to="/invoice-dashboard" className="dropdown-item">
                    Invoice Dashboard
                  </Link>
                </div>
              )}
            </div>
            </>
          )}
          {/* {userData.role === 'superAdmin' && (
            <Link to="/pricing" className="nav-link">
              Pricing
            </Link>
          )} */}
          {userData.access?.includes('accounts') && userData.role != "superAdmin" && (
            <>
              <Link to="/generated-invoices" className="nav-link">Invoices</Link>
            </>
          )}
          {canSeeMarketing && <MarketingDropdown />}
          {isMobile ? (
            <span className="nav-user-mobile">
              {user?.name || user?.email || ''}
              {userData.role && <small>{userData.role}</small>}
            </span>
          ) : (
          <span className="nav-link welcome-tooltip" data-username={user?.name || user?.email || ''}>Welcome</span>
          )}
          <button 
            onClick={handleLogout} 
            className="nav-link" 
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Logout
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
